import type { IconName } from '../components/Icon.js';

/**
 * The single navigation configuration both layouts render (APP_SPEC §33,
 * docs/UI_GUIDELINES.md §5, §8). Desktop and mobile differ only in *how*
 * they present these entries — icon-only bar vs. labelled drawer rows — so
 * neither maintains its own hardcoded list and the two can never drift
 * apart.
 *
 * Entries are ordered by workflow rather than by when they were added:
 * where the player is (Home), what they can start (Games), who they play
 * with (Friends), and what needs a response (Notifications). Account
 * entries follow, separately, because they are about the account rather
 * than about playing.
 *
 * There is no separate "Active Games" destination: in-progress sessions
 * surface directly on Home ("Continue playing") and on Games ("Continue
 * Playing"), so a standalone list would just be a third copy of the same
 * data.
 *
 * Game History is intentionally not a navigation destination — it is an
 * account screen and lives under Settings (`/settings?tab=history`). Game
 * invitations are likewise not a destination of their own: they are acted
 * on from Notifications (APP_SPEC §20, §33).
 */
export interface NavItem {
  /** Stable identity, independent of the route path. */
  key: string;
  to: string;
  /** The accessible name, tooltip, and (in the mobile drawer) visible label. */
  label: string;
  icon: IconName;
  /** Passed to `NavLink`; only the index route needs exact matching. */
  end?: boolean;
}

/** Primary application navigation — the icon row on desktop, the top of the mobile drawer. */
export const PRIMARY_NAV_ITEMS: NavItem[] = [
  { key: 'home', to: '/', label: 'Home', icon: 'home', end: true },
  { key: 'games', to: '/games', label: 'Games', icon: 'games' },
  { key: 'friends', to: '/friends', label: 'Friends', icon: 'friends' },
  { key: 'notifications', to: '/notifications', label: 'Notifications', icon: 'notifications' },
];

/**
 * Account navigation. `profile` is deliberately absent from the desktop
 * header: the avatar + username control there already navigates to the
 * signed-in user's profile, and rendering a second Profile icon beside it
 * would be two controls for one destination. The mobile drawer *does*
 * render it, because a labelled list of destinations reads better with the
 * destination named explicitly than as an avatar in a different section.
 */
export const ACCOUNT_NAV_ITEMS: NavItem[] = [
  { key: 'profile', to: '/profile', label: 'Profile', icon: 'profile' },
  { key: 'settings', to: '/settings', label: 'Settings', icon: 'settings' },
];

/** The admin destination, appended to primary navigation for admins only. */
export const ADMIN_NAV_ITEM: NavItem = {
  key: 'admin',
  to: '/admin',
  label: 'Admin',
  icon: 'admin',
};

/**
 * The primary destinations to render for a given viewer.
 *
 * A signed-out visitor gets **none**. Every destination above except Home
 * requires an account, so the signed-out header and drawer would otherwise
 * present a one-item "navigation" section containing only Home — a section
 * that looks broken and helps nobody, since the brand mark in the header is
 * already the link home. Signing in is what a signed-out visitor needs
 * offered, and the header's Log in / Register controls do that.
 *
 * This is a *visibility* decision only: `/` stays a public route rendered by
 * `DashboardPage`'s signed-out branch, reachable by URL, by the brand link,
 * and by every existing link to it. Route permissions are unchanged
 * (`router.tsx` — Home sits outside `ProtectedRoute`), and neither this
 * function nor anything that calls it grants access to anything.
 */
export function primaryNavItemsFor({
  isSignedIn,
  isAdmin,
}: {
  isSignedIn: boolean;
  isAdmin: boolean;
}): NavItem[] {
  if (!isSignedIn) return [];
  return isAdmin ? [...PRIMARY_NAV_ITEMS, ADMIN_NAV_ITEM] : PRIMARY_NAV_ITEMS;
}

/**
 * The standardized navigation glyph geometry — one size per layout, never
 * a per-icon value (docs/UI_GUIDELINES.md §9). Every file in `NAV_ART` is a
 * square 128px canvas with its artwork fitted to a shared content box, so a
 * single square box keeps aspect ratios intact *and* keeps optical weight
 * consistent between glyphs whose masters differ in coverage.
 *
 * Desktop uses 1.75rem (28px), of which the artwork occupies 112/128 —
 * about 24.5px of visible glyph, matching the header's brand mark and
 * username rather than the 20px control size the icons previously
 * inherited. Mobile keeps the established 1.25rem drawer size, where the
 * glyph is paired with a text label and needs no extra presence.
 */
export const NAV_ICON_CLASS = {
  desktop: 'h-7 w-7',
  mobile: 'h-5 w-5',
} as const;
